import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, onSnapshot } from 'firebase/firestore';

const MetricCard = ({ title, value, footer, isCurrency = true }) => {
    return (
        <div className="metric-card">
            <h4 className="metric-title">{title}</h4>
            <p className="metric-value">
                {isCurrency ? 'RM ' : ''}{value}
            </p>
            {footer && <p className="metric-footer">{footer}</p>}
        </div>
    );
};

const TopCategoriesList = ({ title, data, valueKey, valueLabel, onCategoryClick }) => (
    <div className="form-section">
        <h3>{title}</h3>
        {data.length > 0 ? (
            <ol style={{ paddingLeft: '20px', margin: 0 }}>
                {data.map(item => (
                    <li key={item.category} style={{ marginBottom: '12px', fontSize: '1.1rem' }}>
                        <span>{item.category}</span>
                        {onCategoryClick ? (
                            <button className="product-id-link" onClick={() => onCategoryClick(item.category)}>
                                <strong style={{ color: 'var(--brand-primary)' }}>{`${item[valueKey]} ${valueLabel}`}</strong>
                            </button>
                        ) : (
                            <strong style={{ color: 'var(--brand-primary)' }}>{`RM ${Number(item[valueKey]).toFixed(2)}`}</strong>
                        )}
                    </li>
                ))}
            </ol>
        ) : (<p>No data available for this period.</p>)}
    </div>
);

const CompanyDashboard = ({ onNavigateWithFilter }) => {
    const [companyProfit, setCompanyProfit] = useState(0);
    const [totalInvestment, setTotalInvestment] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const profitGoal = 30000;
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [displayMode, setDisplayMode] = useState('all');
    const [salesData, setSalesData] = useState([]);
    const [productsMap, setProductsMap] = useState(new Map());

    useEffect(() => {
        setLoading(true);
        const productsQuery = query(collection(db, "products"));
        const unsubscribeProducts = onSnapshot(productsQuery, (snapshot) => {
            const prodMap = new Map();
            snapshot.forEach(doc => prodMap.set(doc.id, doc.data()));
            setProductsMap(prodMap);
        }, (err) => setError("Could not load product data."));

        const salesQuery = query(collection(db, "sales"));
        const unsubscribeSales = onSnapshot(salesQuery, (snapshot) => {
            const allSales = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id, saleDate: doc.data().saleDate.toDate() }));
            setSalesData(allSales);
            const cumulativeProfit = allSales.reduce((acc, sale) => acc + (sale.totalProfit || 0), 0);
            setCompanyProfit(cumulativeProfit);
        }, (err) => setError("Could not load sales data."));

        const investmentsQuery = query(collection(db, "investments"));
        const unsubscribeInvestments = onSnapshot(investmentsQuery, (snapshot) => {
            let invested = 0, withdrawn = 0;
            snapshot.forEach(doc => {
                if (doc.data().type === 'Investment') invested += doc.data().amount;
                else if (doc.data().type === 'Withdrawal') withdrawn += doc.data().amount;
            });
            setTotalInvestment(invested - withdrawn);
            setLoading(false);
        }, (err) => setError("Could not load investment data."));

        return () => { unsubscribeProducts(); unsubscribeSales(); unsubscribeInvestments(); };
    }, []);

    const processedData = useMemo(() => {
        const filteredSales = salesData.filter(s => s.saleDate.getFullYear() === selectedYear);
        const monthly = Array(12).fill(null).map(() => ({ totalSales: 0, totalProfit: 0 }));
        const quarters = { Q1: { totalSales: 0, totalProfit: 0 }, Q2: { totalSales: 0, totalProfit: 0 }, Q3: { totalSales: 0, totalProfit: 0 }, Q4: { totalSales: 0, totalProfit: 0 }};
        const categories = {};
        
        filteredSales.forEach(sale => {
            const saleMonth = sale.saleDate.getMonth();
            const product = productsMap.get(sale.productId);
            const category = product ? product.category : 'Unknown';
            const saleAmount = (product ? (product.sellingPriceMYR || 0) : 0) * sale.quantitySold;
            monthly[saleMonth].totalSales += saleAmount;
            monthly[saleMonth].totalProfit += sale.totalProfit;
            if (saleMonth <= 2) { quarters.Q1.totalSales += saleAmount; quarters.Q1.totalProfit += sale.totalProfit; }
            else if (saleMonth <= 5) { quarters.Q2.totalSales += saleAmount; quarters.Q2.totalProfit += sale.totalProfit; }
            else if (saleMonth <= 8) { quarters.Q3.totalSales += saleAmount; quarters.Q3.totalProfit += sale.totalProfit; }
            else { quarters.Q4.totalSales += saleAmount; quarters.Q4.totalProfit += sale.totalProfit; }
            if (!categories[category]) categories[category] = { category, totalProfit: 0, totalQuantity: 0 };
            categories[category].totalProfit += sale.totalProfit;
            categories[category].totalQuantity += sale.quantitySold;
        });

        const categoryArray = Object.values(categories);
        const topByProfit = [...categoryArray].sort((a, b) => b.totalProfit - a.totalProfit).slice(0, 5);
        const topByQuantity = [...categoryArray].sort((a, b) => b.totalQuantity - a.totalQuantity).slice(0, 5);
        
        return { monthly, quarterly: quarters, topByProfit, topByQuantity };
    }, [selectedYear, salesData, productsMap]);

    const years = useMemo(() => {
        const salesYears = salesData.map(s => s.saleDate.getFullYear());
        const currentYear = new Date().getFullYear();
        return [...new Set([...salesYears, currentYear])].sort((a, b) => b - a);
    }, [salesData]);

    const getMonthName = (monthIndex) => new Date(0, monthIndex).toLocaleString('default', { month: 'long' });
    const moreToGo = profitGoal - companyProfit;

    // CORRECTED: Logic for displaying rolling months
    const getRollingMonths = () => {
        const currentYear = new Date().getFullYear();
        if (selectedYear !== currentYear) return processedData.monthly;

        const currentMonthIndex = new Date().getMonth(); // e.g., July is 6
        let startMonthIndex = currentMonthIndex - 2;
        if (startMonthIndex < 0) startMonthIndex = 0;
        
        return processedData.monthly.slice(startMonthIndex, currentMonthIndex + 1);
    };
    const monthlyDataToDisplay = getRollingMonths();
    const hasMonthlyData = monthlyDataToDisplay.some(m => m.totalSales > 0);

    if (loading) return <p>Loading company dashboard...</p>;
    if (error) return <p style={{ color: 'var(--brand-danger)' }}>{error}</p>;

    return (
        <div className="company-dashboard">
            <h2>Company Dashboard</h2>
            <div className="metrics-grid">
                <MetricCard title="Company Profit (Achievement)" value={companyProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} footer="Total profit from all recorded sales" />
                <MetricCard title="Total Investment" value={totalInvestment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} footer="Current capital from all founders" />
                <MetricCard title="Goal" value={profitGoal.toLocaleString()} footer="Target for profit sharing" />
                <div className="metric-card centered-metric"><p className="metric-value large">RM {moreToGo > 0 ? moreToGo.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00"}</p><h4 className="metric-title">More To Go</h4></div>
            </div>
            <hr style={{margin: '2rem 0'}} />
            <h2>Sales Analytics</h2>
            <div className="search-filter-bar" style={{ marginBottom: '1.5rem' }}>
                <div className="filter-group"><label>Year</label><select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}>{years.map(y => <option key={y} value={y}>{y}</option>)}</select></div>
                <div className="filter-group"><label>View</label><select value={displayMode} onChange={e => setDisplayMode(e.target.value)}><option value="all">All</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option></select></div>
            </div>

            {(displayMode === 'monthly' || displayMode === 'all') && (
                <div className="monthly-view" style={{marginBottom: '2rem'}}>
                    <h4>Monthly Breakdown for {selectedYear}</h4>
                    {hasMonthlyData ? (
                        <div className="metrics-grid" style={{gridTemplateColumns: 'repeat(3, 1fr)'}}>
                            {monthlyDataToDisplay.map((m, index) => m.totalSales > 0 && (
                                <MetricCard key={index} title={getMonthName(processedData.monthly.indexOf(m))} value={m.totalSales.toFixed(2)} footer={`Profit: RM ${m.totalProfit.toFixed(2)}`} />
                            ))}
                        </div>
                    ) : (<p>No monthly sales data for {selectedYear}.</p>)}
                </div>
            )}
            
            {(displayMode === 'quarterly' || displayMode === 'all') && (
                <div className="quarterly-view" style={{marginBottom: '2rem'}}>
                    <h4>Quarterly Breakdown for {selectedYear}</h4>
                    <div className="metrics-grid">
                        <MetricCard title="Quarter 1 (Jan-Mar)" value={processedData.quarterly.Q1.totalSales.toFixed(2)} footer={`Profit: RM ${processedData.quarterly.Q1.totalProfit.toFixed(2)}`} />
                        <MetricCard title="Quarter 2 (Apr-Jun)" value={processedData.quarterly.Q2.totalSales.toFixed(2)} footer={`Profit: RM ${processedData.quarterly.Q2.totalProfit.toFixed(2)}`} />
                        <MetricCard title="Quarter 3 (Jul-Sep)" value={processedData.quarterly.Q3.totalSales.toFixed(2)} footer={`Profit: RM ${processedData.quarterly.Q3.totalProfit.toFixed(2)}`} />
                        <MetricCard title="Quarter 4 (Oct-Dec)" value={processedData.quarterly.Q4.totalSales.toFixed(2)} footer={`Profit: RM ${processedData.quarterly.Q4.totalProfit.toFixed(2)}`} />
                    </div>
                </div>
            )}
            <div className="top-categories-view" style={{marginTop: '1.5rem'}}>
                <h4>Top 5 Categories for {selectedYear}</h4>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem'}}>
                    <TopCategoriesList title="Top 5 by Profit" data={processedData.topByProfit} valueKey="totalProfit" valueLabel="RM" />
                    <TopCategoriesList title="Top 5 by Quantity Sold" data={processedData.topByQuantity} valueKey="totalQuantity" valueLabel="units" onCategoryClick={(category) => onNavigateWithFilter('salesHistory', { type: 'category', value: category })} />
                </div>
            </div>
        </div>
    );
};

export default CompanyDashboard;